import {
  API_FETCH_CONVERSATION,
  API_SUBMIT_FEEDBACK,
  API_USER_CLIENTS,
  API_VALIDATE_PASSWORD,
} from "./constants";
import { axiosInstance } from "./httpClient";

export const validatePasscode = async (data) => {
  try {
    const response = await axiosInstance.post(API_VALIDATE_PASSWORD, data);
    return response.data;
  } catch (error) {
    const errMsg = error?.response?.data?.error || error.message;

    throw new Error(errMsg);
  }
};

export const getAllConversations = async () => {
  try {
    const response = await axiosInstance.get(API_FETCH_CONVERSATION);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching data");
  }
};

// Persist the post-stream conversation snapshot. The previous signature
// took a `setStatus` setter and called it imperatively from inside the
// service — inversion of control we don't accept anywhere else. The
// caller (`hooks/chat/useStoreConversation`) now wraps this in a
// useState/useMutation and owns the status state itself.
export const storeAllData = async (clientId, activeConversation) => {
  const response = await axiosInstance.post(`/store/${clientId}`, {
    activeConversation,
  });
  return response.data;
};

// Submit user feedback on a single assistant message. Persisted server-side to
// the dedicated feedback table and forwarded to Slack. `rating` is
// "positive" | "negative"; `reason` (negative-only, single-select) and
// `comment` are optional.
export const submitFeedback = async (
  clientId,
  { conversationId, messageId, rating, reason = null, comment = "" },
) => {
  const response = await axiosInstance.post(
    `${API_SUBMIT_FEEDBACK}/${clientId}`,
    { conversationId, messageId, rating, reason, comment },
  );
  return response.data;
};

const getFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, ""));
    } catch {
      return utf8Match[1].replace(/["']/g, "");
    }
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? null;
};

export const downloadFullTable = async (tableId, clientId) => {
  try {
    const response = await axiosInstance.post(
      "/chat/get_table",
      {
        table_id: tableId,
        client_id: clientId,
      },
      { responseType: "blob" },
    );

    if (!(response.status >= 200 && response.status < 300)) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return {
      blob: response.data,
      filename:
        getFilenameFromContentDisposition(
          response.headers?.["content-disposition"] ??
            response.headers?.get?.("content-disposition"),
        ) ?? `kcubed_copilot_table_${tableId}.csv`,
    };
  } catch (error) {
    console.error("Error downloading full table:", error);
    throw error;
  }
};

export const getUserClients = async () => {
  try {
    const response = await axiosInstance.get(API_USER_CLIENTS);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching data");
  }
};
